<template>
    <div>
        <md-button class="md-primary md-just-icon md-round" @click="showCreateIssueDialog">
            <md-icon>add</md-icon>
            <md-tooltip md-direction="left">Create issue</md-tooltip>
        </md-button>

        <md-dialog class="create-mobile-container" :md-active.sync="showDialog">
            <md-dialog-title>Create New Issue</md-dialog-title>
            <md-dialog-content>
                <form @submit.prevent="createIssue">
                    <div class="md-layout">
                        <div class="md-layout-item md-size-100">
                            <md-field>
                                <label>Title</label>
                                <md-input v-model="issueForm.title" required maxlength="100"></md-input>
                            </md-field>
                        </div>

                        <div class="md-layout-item md-size-100">
                            <md-field>
                                <label>Status</label>
                                <md-select v-model="issueForm.status" required>
                                    <md-option
                                        v-for="(status, index) in issueStatus"
                                        :key="index"
                                        :value="status.issueStatusId"
                                    >
                                        {{ status.statusName }}
                                    </md-option>
                                </md-select>
                            </md-field>
                        </div>

                        <div class="md-layout-item md-size-100">
                            <md-field>
                                <label>Related Entity Type</label>
                                <md-select v-model="issueForm.relatedEntityType" required>
                                    <md-option value="Stop">Stop</md-option>
                                    <md-option value="Trip">Trip</md-option>
                                    <md-option value="Shipment">Shipment</md-option>
                                    <md-option v-if="!isTeamMember" value="CustomerInvoice">Customer Invoice</md-option>
                                    <md-option v-if="!isCustomerAdmin" value="TeamMemberInvoice">
                                        Team Member Invoice
                                    </md-option>
                                </md-select>
                            </md-field>
                        </div>

                        <div class="md-layout-item md-size-100">
                            <md-field>
                                <label>Related Entity Reference</label>
                                <md-input v-model="issueForm.relatedEntityRef" required></md-input>
                            </md-field>
                        </div>

                        <div class="md-layout-item md-size-100">
                            <md-field>
                                <label>Initial Note</label>
                                <md-textarea
                                    class="initial-note-textarea"
                                    v-model="issueForm.initialNote"
                                ></md-textarea>
                            </md-field>
                        </div>
                    </div>
                </form>
            </md-dialog-content>
            <md-dialog-actions class="actions-custom">
                <md-button class="md-default" @click="showDialog = false">Cancel</md-button>
                <md-button class="md-primary" @click="createIssue" :disabled="!isFormValid">Create</md-button>
            </md-dialog-actions>
        </md-dialog>
    </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import { ISSUE_STATUSES } from '@/utils/constants';
import { handleRequests, showErrorMessage } from '@/helpers';
import { GeneralMixin } from '@/mixins';

export default {
    name: 'CreateIssue',
    props: {
        relatedEntityType: {
            type: String,
            default: null
        },
        relatedEntityRef: {
            type: String,
            default: null
        },
        openDialog: {
            type: Boolean,
            default: false
        }
    },
    created() {
        this.showDialog = this.openDialog;
    },
    watch: {
        showDialog() {
            if (this.showDialog === false) {
                this.resetForm();
                this.removeActionQuery('action');
            } else if (this.showDialog) {
                this.issueForm.relatedEntityType = this.relatedEntityType;
                this.issueForm.relatedEntityRef = this.relatedEntityRef;
            }
        }
    },
    data() {
        return {
            showDialog: false,
            issueForm: {
                status: 1,
                relatedEntityType: this.relatedEntityType,
                relatedEntityRef: this.relatedEntityRef,
                initialNote: null,
                title: null
            },
            issueStatus: ISSUE_STATUSES
        };
    },
    mixins: [GeneralMixin],
    computed: {
        isFormValid() {
            return (
                this.issueForm.status !== null &&
                (this.issueForm.relatedEntityType !== null && this.issueForm.relatedEntityType !== '') &&
                this.issueForm.relatedEntityRef !== null &&
                (this.issueForm.title !== null && this.issueForm.title !== '')
            );
        },
        ...mapGetters({
            user: 'user/user',
            isCustomerAdmin: 'user/isCustomerAdmin',
            isTeamMember: 'user/isTeamMember'
        })
    },
    methods: {
        ...mapMutations({
            incrementIssues: 'team/INCREMENT_PENDING_ISSUES',
            decrementIssues: 'team/DECREMENT_PENDING_ISSUES'
        }),
        async showCreateIssueDialog() {
            this.showDialog = true;
        },
        async createIssue() {
            if (!this.isFormValid) {
                this.$notify({
                    message: 'Please fill in all required fields',
                    type: 'warning'
                });
                return;
            }
            let resetForm = true;

            try {
                const payload = {
                    method: 'post',
                    data: {
                        issueStatusId: this.issueForm.status,
                        issueType: this.issueForm.relatedEntityType,
                        note: this.issueForm.initialNote,
                        teamId: this.user.teamId,
                        tripDate: null,
                        relatedEntityRef: this.issueForm.relatedEntityRef.trim(),
                        title: this.issueForm.title.trim()
                    }
                };
                this.$_handleLoaderState(true, '...CREATING');

                const api = `/api/issue/create-issue`;
                const response = await handleRequests(api, payload);

                if (response.data) 
                    this.$emit('issueCreated', response.data);

                this.$notify({
                    message: 'Issue created successfully',
                    type: 'success'
                });

                if (this.issueStatus.find((x) => x.statusName === 'Pending')) 
                    this.incrementIssues();
            } catch (error) {
                const message = 'There was an error creating a new issue';
                showErrorMessage(this, message, error);

                if (error && error.status && error.status === 400) {
                    resetForm = false;
                }
            } finally {
                if (resetForm) {
                    this.resetForm();
                    this.showDialog = false;
                }

                this.$_handleLoaderState(false);
            }
        },
        resetForm() {
            this.issueForm = {
                status: 1,
                relatedEntityType: null,
                relatedEntityRef: null,
                initialNote: null,
                title: null
            };
        },
        removeActionQuery(param) {
            const query = { ...this.$route.query };
            delete query[param];

            this.$router.replace({ query });
        }
    }
};
</script>

<style scoped>
.actions-custom {
    padding: 0px 24px 24px;
}

.initial-note-textarea {
    resize: none !important;
}

@media screen and (max-width: 768px) {
    .create-mobile-container {
        max-height: 80%;
    }
}
</style>
